import ErrorMessage from '@/components/common/error-message/ErrorMessage';
import GoBack from '@/components/common/go-back/GoBack';
import AddDiscountModal from '@/pages/protected/Sales/SaleDetail/components/add-discount-modal/AddDiscountModal';
import AddPaymentModal from '@/pages/protected/Sales/SaleDetail/components/add-payment-modal/AddPaymentModal';
import CancelSaleModal from '@/pages/protected/Sales/SaleDetail/components/cancel-sale-modal/CancelSaleModal';
import NoData from '@/components/common/no-data/NoData';
import { Skeleton } from '@/components/ui/skeleton';
import useSaleDetail from '@/hooks/pages/useSaleDetail';
import { PaymentsTable } from './components/payments-table/PaymentsTable';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ReservationsList } from './components/reservations-list.tsx/ReservationsList';
import { Button } from '@/components/ui/button';
import PaymentsStats from './components/payments-stats/PaymentsStats';
import { SaleInfo } from './components/sale-info/SaleInfo';
import { UserInfo } from './components/user-info/UserInfo';
import { ParticipantsInfo } from './components/participants-info/ParticipantsInfo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';

const SaleDetail = () => {
  const { t } = useTranslation();
  const {
    sale,
    organization,
    paymentType,
    onSubmit,
    addPaymentModal,
    setAddPaymentModal,
    addDiscountModal,
    setAddDiscountModal,
    cancelSaleModal,
    setCancelSaleModal,
    isPendingPatchCancelSale,
    mutate,
    isPending,
    isLoading,
    isError
  } = useSaleDetail();

  if (isLoading) {
    return (
      <>
        <GoBack />
        <Skeleton className="w-full h-4/5" />
      </>
    );
  }

  if (!sale || !organization || !paymentType) {
    return (
      <>
        <GoBack />
        <NoData />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <GoBack />
        <ErrorMessage />
      </>
    );
  }

  const sortedReservations = sale.reservations
    .slice()
    .sort((a, b) => a.order - b.order);

  return (
    <>
      <GoBack />
      <div className="flex flex-col w-full gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card className="p-0 rounded-md">
            <CardHeader>
              <CardTitle className="text-xl">
                {t('sales.titles.sale')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SaleInfo sale={sale} />
            </CardContent>
          </Card>
          <Card className="p-0 rounded-md">
            <CardHeader>
              <CardTitle className="text-xl">
                {t('sales.titles.client')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UserInfo sale={sale} />
            </CardContent>
          </Card>
        </div>

        <Accordion
          type="single"
          collapsible
          className="w-full"
          defaultValue="reservations"
        >
          <AccordionItem value="reservations">
            <Card className="p-0 rounded-md">
              <CardHeader>
                <AccordionTrigger className="p-0">
                  <CardTitle className="text-xl">
                    {t('sales.titles.reservations')}
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <ReservationsList
                    reservations={sortedReservations}
                    organization={organization}
                  />
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
        </Accordion>

        <Card className="p-0 rounded-md">
          <CardHeader>
            <CardTitle className="text-xl">
              {t('sales.titles.participants')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ParticipantsInfo participants={sale.participants} />
          </CardContent>
        </Card>

        <Card className="p-0 rounded-md">
          <CardHeader>
            <CardTitle className="text-xl">
              {t('sales.titles.payments')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PaymentsStats sale={sale} organization={organization} />
            <PaymentsTable
              sale={sale}
              organization={organization}
              paymentType={paymentType}
            />
            <div className="grid gap-4 mt-6 grid-cols1 md:grid-cols-3">
              <Button
                variant={'outline'}
                onClick={() => setAddDiscountModal(true)}
                disabled={!sale.amountPending}
              >
                {t('sales.buttons.addDiscount')}
              </Button>
              <Button
                onClick={() => setAddPaymentModal(true)}
                disabled={!sale.amountPending}
              >
                {t('sales.buttons.addPayment')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setCancelSaleModal(true)}
              >
                {t('sales.buttons.cancelSale')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <AddDiscountModal
        sale={sale}
        organization={organization}
        paymentType={paymentType}
        open={addDiscountModal}
        onOpenChange={setAddDiscountModal}
        onSave={(formValues) => {
          mutate(formValues);
          setAddDiscountModal(false);
        }}
        loading={isPending}
      />
      <AddPaymentModal
        sale={sale}
        organization={organization}
        paymentType={paymentType}
        open={addPaymentModal}
        onOpenChange={setAddPaymentModal}
        onSave={(formValues) => {
          mutate(formValues);
          setAddPaymentModal(false);
        }}
        loading={isPending}
      />
      <CancelSaleModal
        open={cancelSaleModal}
        onOpenChange={setCancelSaleModal}
        loading={isPendingPatchCancelSale}
        onSave={onSubmit}
      />
    </>
  );
};

export default SaleDetail;
